import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import {
  tokens,
  fmtNumber,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import PrepPacketDetail from "../components/PrepPacketDetail";
import {
  getAppointments,
  markPrepReviewed,
  buildDaySignals,
} from "../services/previsitPlanningService";
import { DATE_KEYS, DATE_OPTIONS, PREP_STATUS, PREP_STATUS_META } from "../common/constants";

/**
 * Smart Pre-Visit Planning (Módulo D) — master-detail:
 * izquierda cola de citas del día, derecha prep packet (huddle sheet).
 */
export default function PrevisitPlanningPage() {
  const [dateKey, setDateKey] = useState(DATE_KEYS.TODAY);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [reviewSaving, setReviewSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setSelectedId(null);
    getAppointments(dateKey).then((rows) => {
      if (!active) return;
      setAppointments(rows);
      setSelectedId(rows[0]?.id ?? null);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [dateKey]);

  const selected = useMemo(
    () => appointments.find((a) => a.id === selectedId) || null,
    [appointments, selectedId]
  );

  const signals = useMemo(() => {
    const s = buildDaySignals(appointments);
    return [
      { label: "Appointments", value: fmtNumber(s.appointments), hint: "Scheduled visits for the selected day" },
      {
        label: "Prep ready",
        value: `${s.prepReady}/${s.appointments}`,
        tone: s.prepReady === s.appointments && s.appointments > 0 ? "ok" : "warn",
        hint: "Prep packets ready or already reviewed",
      },
      { label: "Suspected HCC", value: fmtNumber(s.suspectedHcc), tone: "info", hint: "HCC conditions to address across the day" },
      { label: "Open care gaps", value: fmtNumber(s.openGaps), tone: s.openGaps > 0 ? "warn" : "ok" },
      { label: "Meds to reconcile", value: fmtNumber(s.medsToReconcile), tone: "muted" },
    ];
  }, [appointments]);

  const handleMarkReviewed = async (appointmentId) => {
    setReviewSaving(true);
    // Optimistic UI — cambio solo local hasta que exista
    // PUT /api/previsit/huddles/{id}/review en el backend.
    setAppointments((prev) =>
      prev.map((a) => (a.id === appointmentId ? { ...a, status: PREP_STATUS.REVIEWED } : a))
    );
    await markPrepReviewed(appointmentId);
    setReviewSaving(false);
  };

  const columns = [
    { key: "time", header: "Time", mono: true, width: 64 },
    {
      key: "patient",
      header: "Patient",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.ink, lineHeight: 1.3 }}>
            {row.patient.name}
          </Typography>
          <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 11.5, color: tokens.ink3 }}>
            {row.patient.cipId}
          </Typography>
        </Box>
      ),
    },
    { key: "visitType", header: "Visit" },
    {
      key: "hcc",
      header: "HCC",
      align: "right",
      mono: true,
      render: (row) => row.suspectedHcc.length || "—",
    },
    {
      key: "gaps",
      header: "Gaps",
      align: "right",
      mono: true,
      render: (row) => row.careGaps.length || "—",
    },
    {
      key: "status",
      header: "Prep status",
      render: (row) => {
        const meta = PREP_STATUS_META[row.status];
        return <StatusPill tone={meta.tone} label={meta.label} dense />;
      },
    },
  ];

  return (
    <ClinicalPageShell
      title="Pre-Visit Planning"
      subtitle="Huddle sheet per appointment: suspected HCCs, open care gaps, meds and labs — everything actionable before the patient walks in."
      moduleTag="Module D"
      devPreview
      actions={
        <Select
          size="small"
          value={dateKey}
          onChange={(e) => setDateKey(e.target.value)}
          sx={{
            minWidth: 140,
            bgcolor: tokens.surface,
            fontFamily: tokens.fontUI,
            fontSize: 13,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.line },
          }}
        >
          {DATE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ flex: "0 1 45%", minWidth: 380 }}>
          <ClinicalDataGrid
            columns={columns}
            rows={appointments}
            rowKey={(row) => row.id}
            loading={loading}
            onRowClick={(row) => setSelectedId(row.id)}
            isRowSelected={(row) => row.id === selectedId}
            emptyMessage="No appointments scheduled for the selected day."
            maxHeight={640}
          />
        </Box>
        <Box sx={{ flex: "1 1 480px", minWidth: 380 }}>
          <PrepPacketDetail
            appointment={selected}
            onMarkReviewed={handleMarkReviewed}
            reviewSaving={reviewSaving}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
